/**
 * Hook useCampaignSegmentPreview
 * 
 * Busca preview dos contatos que match o segmento definido.
 * Usa debounce para evitar requisições excessivas.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { CampaignSegment, SegmentPreviewContact, isSegmentValid } from '@/types/campaign-segments';

// Logger para depuração
const logger = createLogger('CampaignSegmentPreview');

/**
 * Interface do retorno do hook
 */
export interface CampaignSegmentPreviewResult {
  contacts: SegmentPreviewContact[];
  totalCount: number;
  isTruncated: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Limite de preview para evitar sobrecarga
 */
const PREVIEW_LIMIT = 500;

/**
 * Delay do debounce em ms
 */
const DEBOUNCE_DELAY = 600;

/**
 * Hook para buscar preview de contatos do segmento
 * 
 * @param segment - Segmento com grupos e filtros
 * @param enabled - Se deve executar a query
 */
export function useCampaignSegmentPreview(
  segment: CampaignSegment,
  enabled: boolean = true
): CampaignSegmentPreviewResult {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  
  // Estado para debounce
  const [debouncedSegment, setDebouncedSegment] = useState<CampaignSegment>(segment);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Debounce do segmento
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      setDebouncedSegment(segment);
    }, DEBOUNCE_DELAY);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [segment]);

  // Query para buscar contatos
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['segment-preview', tenantId, JSON.stringify(debouncedSegment)],
    queryFn: async () => {
      if (!tenantId || !isSegmentValid(debouncedSegment)) {
        return { contacts: [], total_count: 0 };
      }

      logger.debug('Buscando preview do segmento:', { 
        groupCount: debouncedSegment.groups.length,
        filterCount: debouncedSegment.groups.reduce((sum, g) => sum + g.filters.length, 0)
      });

      // Chamar RPC para buscar contatos que match o segmento
      // A RPC será criada via migration
      const { data, error } = await supabase.rpc('get_campaign_segment_recipients' as never, {
        p_segment: debouncedSegment as unknown as Record<string, unknown>,
        p_limit: PREVIEW_LIMIT,
      } as never);

      if (error) {
        logger.error('Erro ao buscar preview do segmento:', error);
        throw error;
      }

      // A RPC retorna array de contatos
      const contacts = (data || []) as Array<{
        contact_id: string;
        contact_name: string | null;
        contact_phone: string;
        match_reason: string | null;
      }>;

      const totalCount = contacts.length;
      const isTruncated = contacts.length >= PREVIEW_LIMIT;

      logger.debug('Preview carregado:', { 
        contactCount: contacts.length, 
        totalCount,
        isTruncated 
      });

      return {
        contacts: contacts.map(c => ({
          id: c.contact_id,
          name: c.contact_name,
          phone: c.contact_phone,
          match_reason: c.match_reason || undefined,
        })),
        total_count: totalCount,
      };
    },
    enabled: enabled && !!tenantId && isSegmentValid(debouncedSegment),
    staleTime: 30 * 1000, // 30 segundos de cache
    gcTime: 60 * 1000,    // 1 minuto no garbage collector
  });

  // Refetch manual
  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    contacts: data?.contacts || [],
    totalCount: data?.total_count || 0,
    isTruncated: (data?.contacts?.length || 0) >= PREVIEW_LIMIT,
    isLoading,
    isError,
    error: error as Error | null,
    refetch: handleRefetch,
  };
}
